/*!

=========================================================
* Argon Dashboard React - v1.2.1
=========================================================

* Product Page: https://www.creative-tim.com/product/argon-dashboard-react
* Copyright 2021 Creative Tim (https://www.creative-tim.com)
* Licensed under MIT (https://github.com/creativetimofficial/argon-dashboard-react/blob/master/LICENSE.md)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/
import {
  AllInclusive,
  ArrowDownward,
  InfoOutlined
} from "@material-ui/icons";
import ReactTooltip from 'react-tooltip';
import React, { useEffect, useState } from "react";
import {
  Button,
  Col,
  Container,
  Row,
  Card,
  CardHeader,
  CardBody,
  Table
} from "reactstrap";
import { Checkbox } from "@material-ui/core";
import Toastr from "toastr";
import { DateTime } from 'luxon';
import UploadDropzone from "../components/UploadDropzone";
import { useSaveBulkExtractMutation } from "../hooks/useSaveBulkExtractMutation";
import { useGetBulkExtract } from '../hooks/useGetBulkExtract';
import { useQueryParams } from '../hooks/useQueryParams';
import CustomPagination from '../components/CustomPagination';
import { toCustomDateTimeString } from '../utils/toCustomDateTimeString';

const RESULTS_PER_PAGE = 10;
const BulkExtractPage: React.FC = () => {
  const query = useQueryParams()
  const [bulkExtractFile, setBulkExtractFile] = useState<File>();
  const [extractFiltered, setExtractFiltered] = useState<boolean>(true);

  const [pageTable, setPageTable] = useState(() => {
    let page = +(query.get('page') || 0)
    if (isNaN(page) || page <= 0) {
      return 1
    }
    return page
  })

  const {
    isError,
    isLoading,
    data,
    refetch,
  } = useGetBulkExtract(pageTable, RESULTS_PER_PAGE)

  useEffect(() => {
    refetch()
  }, [pageTable])

  const {
    mutate: saveBulkExtract,
    data: saveBulkExtractResponse,
    isLoading: saveBulkExtractLoading,
    isError: saveBulkExtractError,
  } = useSaveBulkExtractMutation();

  useEffect(() => {
    if (saveBulkExtractError) {
      Toastr.error("Something went wrong. Please try again later");
    }
  }, [saveBulkExtractError])

  useEffect(() => {
    if (saveBulkExtractResponse) {
      Toastr.success("Extraction started!");
      setBulkExtractFile(undefined);
    }
  }, [saveBulkExtractResponse])

  const handleExtractSubmit = () => {
    if (!bulkExtractFile) {
      alert("Please upload a excel file.");
      return;
    }

    saveBulkExtract({ file: bulkExtractFile, filtered: extractFiltered })
  }

  const handleDownloadSample = () => {
    window.open("/bulk-extract-sample.xlsx");
  }

  return (
    <>
      <div className="header pb-6 pt-5 pt-md-8"
        style={{
          height: "500px",
          backgroundColor: '#2D315C',
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundImage: "url(/background-header-infinity.jpg)"
        }}
      >
        {/* Mask */}
        <span className="mask bg-gradient-default opacity-8" />
        <Container style={{ display: "flex", flexDirection: "column" }} fluid>
          <Row>
            <Col>
              <div style={{ textAlign: "center" }}>
                <div style={{ display: "inline-block", width: "75%", marginBottom: '10px' }}>
                  <UploadDropzone
                    setFile={setBulkExtractFile}
                    isFileUploaded={!!saveBulkExtractLoading}
                    accept={[
                      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                    ]}
                    icon="/file-types-excel.png"
                    title="Click/drag and drop Excel file for bulk extract."
                  />
                </div>
              </div>
              <div className="text-center">
                {saveBulkExtractLoading ? (
                  <svg height="70px" viewBox="0 0 187.3 93.7" preserveAspectRatio="xMidYMid meet" >
                    <path stroke="rgb(215, 61, 50)" id="outline" fill="none" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10"
                      d="M93.9,46.4c9.3,9.5,13.8,17.9,23.5,17.9s17.5-7.8,17.5-17.5s-7.8-17.6-17.5-17.5c-9.7,0.1-13.3,7.2-22.1,17.1 				c-8.9,8.8-15.7,17.9-25.4,17.9s-17.5-7.8-17.5-17.5s7.8-17.5,17.5-17.5S86.2,38.6,93.9,46.4z" />
                    <path id="outline-bg" opacity="0.05" fill="none" stroke="#fff" stroke-width="4" stroke-linecap="round" stroke-linejoin="round" stroke-miterlimit="10"
                      d="				M93.9,46.4c9.3,9.5,13.8,17.9,23.5,17.9s17.5-7.8,17.5-17.5s-7.8-17.6-17.5-17.5c-9.7,0.1-13.3,7.2-22.1,17.1 				c-8.9,8.8-15.7,17.9-25.4,17.9s-17.5-7.8-17.5-17.5s7.8-17.5,17.5-17.5S86.2,38.6,93.9,46.4z" />
                  </svg>
                ) : (
                  <>
                    <Button
                      color="primary"
                      style={{
                        borderRadius: "50%",
                        width: '50px',
                        height: '50px',
                        padding: '0',
                      }}
                      data-tip="Download Sample"
                      onClick={handleDownloadSample}
                    >
                      <ArrowDownward />
                    </Button>
                    <ReactTooltip place="top" type="dark" effect="float" />
                    <Button
                      color="primary"
                      onClick={handleExtractSubmit}
                      disabled={saveBulkExtractLoading}
                      style={{
                        width: "130px", fontSize: "20px", background: "#FF5252",
                        border: "#FF5252"
                      }}
                      className="btn-sm "
                    >
                      <AllInclusive />
                    </Button>
                    <Checkbox
                      checked={extractFiltered}
                      onChange={(e) => setExtractFiltered(e.target.checked)}
                      inputProps={{ "aria-label": "primary checkbox" }}
                      style={{ color: "#fff" }}
                    />
                      <label style={{ color: "white" }}>Extract filtered data?</label>
                      <div style={{ display: "inline-block" }} 
                        title={`Filtered Data :- When using Filtered Data feature the tool extracts only the main content of an URL.\nFor example :- In a news article URL only the main news article is extracted from the URL.\n\nUnfiltered Data :- When using Unfiltered Data the tool extracts every possible information/content available on the URL.\nFor example :- In a news article URL, all the text including the date of publishing, author of the article, caption of main article images, texts of other news articles is extracted from the URL.`}>
                        <InfoOutlined style={{ color: '#fff', marginLeft: '10px', cursor: 'pointer' }} />
                      </div>
                  </>
                )}
              </div>
            </Col>
          </Row>
        </Container>
        {(data && data.data.length) && <Container className="mt--2" fluid>
          <Row className="mt-5">
            <Col className="mb-5 mb-xl-0">
              <Card className="shadow">
                <CardHeader className="border-0">
                  <h3>Past Extractions</h3>
                </CardHeader>
                <CardBody>
                  <Table className="align-items-center table-flush" responsive>
                    <thead className="thead-light">
                      <tr>
                        <th>ID</th>
                        <th>Progress</th>
                        <th>Status</th>
                        <th>Executed By</th>
                        <th>Executed At</th>
                        <th>Input File</th>
                        <th>Output File</th>
                      </tr>
                    </thead>
                    <tbody>
                      {
                        data.data.map((record) => <tr key={record.id}>
                          <td>{record.id}</td>
                          <td>{`${(record.currentExec || record.currentExec === 0)  ? record.currentExec : "NA" }/${record.totalExec ? record.totalExec : "NA"}`}</td>
                          <td>{record.status}</td>
                          <td>{record.createdBy}</td>
                          <td>{record.createdAt ? toCustomDateTimeString(DateTime.fromISO(record.createdAt)) : 'NA'}</td>
                          <td>
                            <a href={`${record.inputfileUrl}?token=${localStorage.getItem('token')}`} download target='_blank' rel='noreferrer'>
                              <i className="fas fa-download"></i>
                            </a>
                          </td>
                          <td>
                            {record.outputfileUrl ? (<a href={`${record.outputfileUrl}?token=${localStorage.getItem('token')}`} download target='_blank' rel='noreferrer'>
                              <i className="fas fa-download"></i>
                            </a>) :
                              'Not available'}
                          </td>
                        </tr>)
                      }
                    </tbody>
                    <tfoot>
                      <nav style={{ display: "inline-block" }} aria-label="...">
                        <CustomPagination
                          activePage={pageTable}
                          totalResults={data.total}
                          resultsPerPage={RESULTS_PER_PAGE}
                          onChange={setPageTable}
                          label="Table navigation"
                        />
                      </nav>
                    </tfoot>
                  </Table>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>}
      </div>
    </>
  );
};

export default BulkExtractPage;
